import { createAsyncThunk } from "@reduxjs/toolkit";

import * as projectsApi from "../services/projectsApi";
import * as issuesApi from "../services/issuesApi";
import * as sprintsApi from "../services/sprintsApi";
import * as componentsApi from "../services/componentsApi";
import * as versionsApi from "../services/versionsApi";
import * as resolutionsApi from "../services/resolutionsApi";
import * as notificationsApi from "../services/notificationsApi";
import * as filtersApi from "../services/filtersApi";
import * as dependenciesApi from "../services/dependenciesApi";
import * as baselinesApi from "../services/baselinesApi";
import * as schedulingApi from "../services/schedulingApi";
import { getErrorMessage } from "../utils/errorUtils";

export const fetchProjects = createAsyncThunk(
  "projects/fetchProjects",
  async (
    filters: {
      search?: string;
      status?: number | null;
      priority?: number | null;
      page?: number;
      limit?: number;
    } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.listProjects(company_id, company_type, filters);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  "projects/fetchProjectById",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.getProjectById(
        company_id,
        company_type,
        projectId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchProjectByKey = createAsyncThunk(
  "projects/fetchProjectByKey",
  async (projectKey: string, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.getProjectByKey(
        company_id,
        company_type,
        projectKey
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createProject = createAsyncThunk(
  "projects/createProject",
  async (payload: Record<string, any>, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.createProject(company_id, company_type, payload);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async (
    { projectId, payload }: { projectId: number; payload: Record<string, any> },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.updateProject(
        company_id,
        company_type,
        projectId,
        payload
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      await projectsApi.deleteProject(company_id, company_type, projectId);
      return projectId;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const checkProjectKeyAvailable = createAsyncThunk(
  "projects/checkProjectKeyAvailable",
  async (key: string, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.projectKeyAvailable(
        company_id,
        company_type,
        key
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchProjectActivity = createAsyncThunk(
  "projects/fetchProjectActivity",
  async (
    {
      projectId,
      page = 1,
      limit = 20,
    }: { projectId: number; page?: number; limit?: number },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.listProjectActivity(
        company_id,
        company_type,
        projectId,
        page,
        limit
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchProjectMembers = createAsyncThunk(
  "projects/fetchProjectMembers",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.listProjectMembers(
        company_id,
        company_type,
        projectId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const addProjectMember = createAsyncThunk(
  "projects/addProjectMember",
  async (
    {
      projectId,
      userId,
      role,
      userSnapshot,
    }: {
      projectId: number;
      userId: number;
      role: string;
      userSnapshot?: Record<string, any> | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.addProjectMember(
        company_id,
        company_type,
        projectId,
        userId,
        role,
        userSnapshot ?? null
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const removeProjectMember = createAsyncThunk(
  "projects/removeProjectMember",
  async (memberId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      await projectsApi.removeProjectMember(
        company_id,
        company_type,
        memberId
      );
      return memberId;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchBoards = createAsyncThunk(
  "projects/fetchBoards",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.listBoards(company_id, company_type, projectId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const reorderStatuses = createAsyncThunk(
  "projects/reorderStatuses",
  async (
    { boardId, statusIds }: { boardId: number; statusIds: number[] },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.reorderStatuses(
        company_id,
        company_type,
        boardId,
        statusIds
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchLabels = createAsyncThunk(
  "projects/fetchLabels",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.listLabels(company_id, company_type, projectId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchCustomFields = createAsyncThunk(
  "projects/fetchCustomFields",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await projectsApi.listCustomFields(
        company_id,
        company_type,
        projectId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchIssues = createAsyncThunk(
  "projects/fetchIssues",
  async (filters: Record<string, any>, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.listIssues(company_id, company_type, filters);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchIssueById = createAsyncThunk(
  "projects/fetchIssueById",
  async (issueId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.getIssueById(company_id, company_type, issueId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchIssueByKey = createAsyncThunk(
  "projects/fetchIssueByKey",
  async (issueKey: string, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.getIssueByKey(company_id, company_type, issueKey);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createIssue = createAsyncThunk(
  "projects/createIssue",
  async (payload: Record<string, any>, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.createIssue(company_id, company_type, payload);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateIssue = createAsyncThunk(
  "projects/updateIssue",
  async (
    { issueId, payload }: { issueId: number; payload: Record<string, any> },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.updateIssue(
        company_id,
        company_type,
        issueId,
        payload
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const moveIssue = createAsyncThunk(
  "projects/moveIssue",
  async (
    payload: {
      id: number;
      status_id?: number;
      sprint_id?: number | null;
      before_id?: number | null;
      after_id?: number | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.moveIssue(company_id, company_type, payload);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteIssue = createAsyncThunk(
  "projects/deleteIssue",
  async (issueId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      await issuesApi.deleteIssue(company_id, company_type, issueId);
      return issueId;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchSprints = createAsyncThunk(
  "projects/fetchSprints",
  async (
    { projectId, state }: { projectId: number; state?: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const reduxState: any = getState();
      const { company_id, company_type } = reduxState.projects.activeCompany;
      return await sprintsApi.listSprints(
        company_id,
        company_type,
        projectId,
        state
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createSprint = createAsyncThunk(
  "projects/createSprint",
  async (payload: Record<string, any>, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await sprintsApi.createSprint(company_id, company_type, payload);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const startSprint = createAsyncThunk(
  "projects/startSprint",
  async (sprintId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await sprintsApi.startSprint(company_id, company_type, sprintId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const completeSprint = createAsyncThunk(
  "projects/completeSprint",
  async (
    {
      sprintId,
      moveUnfinishedTo,
    }: { sprintId: number; moveUnfinishedTo: "BACKLOG" | number },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await sprintsApi.completeSprint(
        company_id,
        company_type,
        sprintId,
        moveUnfinishedTo
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateSprint = createAsyncThunk(
  "projects/updateSprint",
  async (
    { sprintId, payload }: { sprintId: number; payload: Record<string, any> },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await sprintsApi.updateSprint(
        company_id,
        company_type,
        sprintId,
        payload
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteSprint = createAsyncThunk(
  "projects/deleteSprint",
  async (sprintId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      await sprintsApi.deleteSprint(company_id, company_type, sprintId);
      return sprintId;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const reorderSprints = createAsyncThunk(
  "projects/reorderSprints",
  async (sprintIds: number[], { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await sprintsApi.reorderSprints(
        company_id,
        company_type,
        sprintIds
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchComponents = createAsyncThunk(
  "projects/fetchComponents",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await componentsApi.listComponents(company_id, company_type, projectId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchVersions = createAsyncThunk(
  "projects/fetchVersions",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await versionsApi.listVersions(company_id, company_type, projectId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchResolutions = createAsyncThunk(
  "projects/fetchResolutions",
  async (projectId: number | undefined, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await resolutionsApi.listResolutions(company_id, company_type, projectId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  "projects/fetchNotifications",
  async (
    filters: { unread_only?: boolean; page?: number; limit?: number } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await notificationsApi.listForMe(company_id, company_type, filters);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  "projects/markNotificationRead",
  async (id: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await notificationsApi.markRead(company_id, company_type, id);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  "projects/markAllNotificationsRead",
  async (_: void, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await notificationsApi.markAllRead(company_id, company_type);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchSavedFilters = createAsyncThunk(
  "projects/fetchSavedFilters",
  async (projectId: number | undefined, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      const [mine, shared] = await Promise.all([
        filtersApi.listMine(company_id, company_type, projectId),
        filtersApi.listShared(company_id, company_type, projectId),
      ]);
      const mineList = mine?.filters || [];
      const sharedList = (shared?.filters || []).filter(
        (sf: any) => !mineList.find((m: any) => m.id === sf.id)
      );
      return [...mineList, ...sharedList];
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const bulkUpdateIssues = createAsyncThunk(
  "projects/bulkUpdateIssues",
  async (
    payload: { issue_ids: number[]; updates: Record<string, any> },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.bulkUpdateIssues(company_id, company_type, payload);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const bulkDeleteIssues = createAsyncThunk(
  "projects/bulkDeleteIssues",
  async (issue_ids: number[], { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.bulkDeleteIssues(company_id, company_type, issue_ids);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const bulkTransitionIssues = createAsyncThunk(
  "projects/bulkTransitionIssues",
  async (
    payload: { issue_ids: number[]; status_id: number; resolution_id?: number | null },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await issuesApi.bulkTransitionIssues(company_id, company_type, payload);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/* ─── R2: Dependencies ─── */

export const fetchDependencies = createAsyncThunk(
  "projects/fetchDependencies",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      const data = await dependenciesApi.listByProject(company_id, company_type, projectId);
      return data?.dependencies || [];
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const addDependency = createAsyncThunk(
  "projects/addDependency",
  async (
    payload: { project_id: number; predecessor_id: number; successor_id: number; link_type?: string; lag_days?: number },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      const data = await dependenciesApi.addDependency(company_id, company_type, payload);
      return data?.dependency || data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const removeDependency = createAsyncThunk(
  "projects/removeDependency",
  async (id: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      await dependenciesApi.removeDependency(company_id, company_type, id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/* ─── R2: Baselines ─── */

export const fetchBaselines = createAsyncThunk(
  "projects/fetchBaselines",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      const data = await baselinesApi.listBaselines(company_id, company_type, projectId);
      return data?.baselines || [];
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createBaseline = createAsyncThunk(
  "projects/createBaseline",
  async (
    payload: { project_id: number; name: string; description?: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      const data = await baselinesApi.createBaseline(company_id, company_type, payload);
      return data?.baseline || data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchBaselineDetail = createAsyncThunk(
  "projects/fetchBaselineDetail",
  async (id: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      const data = await baselinesApi.getBaselineDetail(company_id, company_type, id);
      return data?.baseline || data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteBaseline = createAsyncThunk(
  "projects/deleteBaseline",
  async (id: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      await baselinesApi.deleteBaseline(company_id, company_type, id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/* ─── R2: Scheduling ─── */

export const autoSchedule = createAsyncThunk(
  "projects/autoSchedule",
  async (
    payload: { project_id: number; force_reschedule?: boolean; project_start_date?: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await schedulingApi.autoSchedule(company_id, company_type, payload);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getCriticalPath = createAsyncThunk(
  "projects/getCriticalPath",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      return await schedulingApi.getCriticalPath(company_id, company_type, projectId);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchCapacity = createAsyncThunk(
  "projects/fetchCapacity",
  async (projectId: number, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const { company_id, company_type } = state.projects.activeCompany;
      const data = await schedulingApi.getCapacity(company_id, company_type, projectId);
      return data?.capacity || [];
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

